<template>
  <div class="goods-management">

    <!-- 搜索和工具栏 -->
    <el-card class="search-card" shadow="never">
      <div class="search-section">
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="search-input-group">
              <el-select 
                v-model="searchForm.select" 
                placeholder="搜索类型"
                class="search-type-select"
                size="medium"
              >
                <el-option
                  v-for="(label, value) in searchTypeLabels"
                  :key="value"
                  :label="label"
                  :value="value"
                />
              </el-select>
              <el-autocomplete
                v-model="searchForm.input"
                :fetch-suggestions="querySearchAsync"
                placeholder="请输入搜索内容"
                class="search-input"
                size="medium"
                @select="handleSelect"
                @keyup.enter="handleSearch"
              >
                <template slot="append">
                  <el-button 
                    icon="el-icon-search" 
                    @click="handleSearch"
                    class="search-button"
                  />
                </template>
              </el-autocomplete>
            </div>
          </el-col>
          <el-col :span="6">
            <el-select 
              v-model="selectedLive" 
              placeholder="选择直播场次" 
              class="live-select"
              size="medium"
              clearable
            >
              <el-option
                v-for="item in liveOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-col>
          <el-col :span="10">
            <div class="action-buttons">
              <el-button 
                type="success" 
                icon="el-icon-download"
                @click="handleExport"
                size="medium"
                :loading="exporting"
                :disabled="exporting"
              >
                {{ exporting ? '导出中...' : '导出商品' }}
              </el-button>
              <el-button 
                type="warning" 
                icon="el-icon-refresh"
                @click="handleRefresh"
                size="medium"
              >
                刷新
              </el-button>
            </div>
          </el-col>
        </el-row>
      </div>
    </el-card>

    <!-- 表格区域 -->
    <el-card class="table-card" shadow="always">
      <div class="table-header">
        <div class="table-info">
          <span class="table-title">商品列表</span>
          <el-tag type="info" size="small" class="table-count">
            共 {{ goodsList.length }} 条记录
          </el-tag>
        </div>
        <div class="table-actions">
          <el-button 
            size="small" 
            type="primary" 
            icon="el-icon-plus"
            @click="handleAdd"
          >
            新增商品
          </el-button>
        </div>
      </div>

      <BaseTable
        :data="goodsList"
        :loading="loading"
        :max-height="tableHeight"
        :empty-text="emptyText"
        class="goods-table"
        @selection-change="handleSelectionChange"
        @sort-change="handleSortChange"
      >
        <el-table-column
          label="品牌名称"
          prop="brand"
          sortable
          min-width="15%"
          class-name="brand-column"
        >
          <template slot-scope="{ row }">
            <div class="brand-cell">
              <el-tag 
                v-if="row.brand" 
                size="small" 
                type="primary"
                class="brand-tag"
              >
                {{ row.brand }}
              </el-tag>
              <span v-else class="empty-text">-</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column type="expand">
          <template slot-scope="props">
            <el-form label-position="left" inline class="demo-table-expand">
              <el-form-item label="品牌名称" v-if="props.row.brand">
                <span>{{ props.row.brand }}</span>
              </el-form-item>
              <el-form-item label="商品名称" v-if="props.row.name">
                <span>{{ props.row.name }}</span>
              </el-form-item>
              <el-form-item label="所属店铺" v-if="props.row.shop">
                <span>{{ props.row.shop }}</span>
              </el-form-item>
              <el-form-item label="商品属性" v-if="props.row.properties">
                <span>{{ props.row.properties }}</span>
              </el-form-item>
              <el-form-item label="商品规格" v-if="props.row.specs">
                <span>{{ props.row.specs }}</span>
              </el-form-item>
              <el-form-item label="商品位置" v-if="props.row.location">
                <span>{{ props.row.location }}</span>
              </el-form-item>
              <el-form-item label="直播价" v-if="props.row.lowPrice">
                <span>{{ props.row.lowPrice }}</span>
              </el-form-item>
              <el-form-item label="商品已拥有" v-if="props.row.hasString">
                <span>{{ props.row.hasString }}</span>
              </el-form-item>
              <el-form-item label="商品描述" v-if="props.row.description">
                <span>{{ props.row.description }}</span>
              </el-form-item>
            </el-form>
          </template>
        </el-table-column>

        <el-table-column
          label="商品名称"
          prop="name"
          sortable
          min-width="25%"
          class-name="name-column"
        >
          <template slot-scope="{ row }">
            <div class="name-cell">
              <span class="product-name">{{ row.name || '-' }}</span>
              <span class="product-shop" v-if="row.shop">{{ row.shop }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="属性"
          prop="properties"
          sortable
          min-width="15%"
          class-name="properties-column"
        >
          <template slot-scope="{ row }">
            <div class="properties-cell">
              <el-tag 
                v-if="row.properties" 
                size="small" 
                type="info"
                class="property-tag"
              >
                {{ row.properties }}
              </el-tag>
              <span v-else class="empty-text">-</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="状态"
          prop="state"
          sortable
          min-width="10%"
          class-name="state-column"
        >
          <template slot-scope="{ row }">
            <el-tag 
              :type="getStatusType(row.state)" 
              size="small"
            >
              {{ row.state || '未知' }}
            </el-tag>
          </template>
        </el-table-column>

        <template #actions="{ row, index }">
          <div class="action-buttons">
            <el-button
              size="mini"
              type="primary"
              icon="el-icon-view"
              @click="handleView(row)"
              class="action-btn view-btn"
            >
              查看
            </el-button>
            <el-button
              size="mini"
              type="success"
              icon="el-icon-edit"
              @click="handleEdit(row)"
              class="action-btn edit-btn"
            >
              修改
            </el-button>
            <el-button
              size="mini"
              type="warning"
              icon="el-icon-video-camera"
              @click="handleAddToLive(row)"
              class="action-btn live-btn"
            >
              直播
            </el-button>
          </div>
        </template>
      </BaseTable>
    </el-card>
  </div>
</template>

<script>
import { goodsApi, liveApi } from '@/api'
import { format } from '@/utils/format'
import { SEARCH_TYPE_LABELS } from '@/constants/enums'
import BaseTable from '@/components/common/BaseTable.vue'

export default {
  name: 'GoodsList',
  components: {
    BaseTable
  },
  data () {
    return {
      searchForm: {
        input: '',
        select: '2'
      },
      selectedLive: '',
      goodsList: [],
      liveOptions: [],
      loading: false,
      exporting: false,
      emptyText: '暂无数据',
      tableHeight: window.innerHeight * 0.6,
      searchTypeLabels: SEARCH_TYPE_LABELS
    }
  },
  mounted () {
    this.getAllLives()
  },
  methods: {
    format,

    // 获取状态类型
    getStatusType (state) {
      const statusMap = {
        '在售': 'success',
        '缺货': 'warning',
        '下架': 'danger',
        '预售': 'info'
      }
      return statusMap[state] || 'info'
    },

    // 新增商品
    handleAdd () {
      const routeUrl = this.$router.resolve({ path: '/add' })
      window.open(routeUrl.href, '_blank')
    },

    // 刷新
    handleRefresh () {
      this.searchForm.input = ''
      this.selectedLive = ''
      this.goodsList = []
      this.$message.success('已清空搜索结果')
    },

    // 搜索建议
    async querySearchAsync (queryString, cb) {
      if (!queryString) {
        cb(null, [])
        return
      }

      try {
        const suggestions = await goodsApi.getSearchSuggestion({
          input: queryString,
          type: this.searchForm.select
        })

        const results = suggestions.map(item => ({ value: item }))
        cb(null, results)
      } catch (error) {
        cb(error)
      }
    },

    handleSelect (item) {
      this.searchForm.input = item.value
    },

    // 搜索商品
    async handleSearch () {
      this.loading = true
      this.emptyText = '数据加载中'

      try {
        let result = []

        switch (this.searchForm.select) {
          case '1':
            result = await goodsApi.searchByName({ input: this.searchForm.input })
            break
          case '2':
            result = await goodsApi.searchByBrandName({ input: this.searchForm.input })
            break
          case '3':
            result = await goodsApi.searchByProperty({ input: this.searchForm.input })
            break
          case '4':
            result = await goodsApi.getAllGoods()
            break
        }

        this.goodsList = result.map(item => ({
          ...item,
          hasString: Array.isArray(item.has) ? item.has.join(' · ') : ''
        }))

        this.emptyText = '暂无数据'
      } catch (error) {
        this.emptyText = '暂无数据'
        this.goodsList = []
      } finally {
        this.loading = false
      }
    },

    // 获取直播场次
    async getAllLives () {
      try {
        const lives = await liveApi.getAllLives()
        this.liveOptions = lives.map(live => ({
          value: live.id,
          label: live.time.substring(0, live.time.indexOf(' '))
        }))
      } catch (error) {
        console.error('获取直播场次失败:', error)
      }
    },

    // 查看商品
    handleView (row) {
      const routeUrl = this.$router.resolve({
        path: '/show',
        query: { id: row.id }
      })
      window.open(routeUrl.href, '_blank')
    },

    // 编辑商品
    handleEdit (row) {
      const routeUrl = this.$router.resolve({
        path: '/update',
        query: { id: row.id }
      })
      window.open(routeUrl.href, '_blank')
    },

    // 添加到直播
    async handleAddToLive (row) {
      if (!this.selectedLive) {
        this.$message.warning('请选择直播场次')
        return
      }

      const selectedLiveOption = this.liveOptions.find(
        option => option.value === this.selectedLive
      )
      const selectedLiveTime = selectedLiveOption ? selectedLiveOption.label : ''

      try {
        await this.$confirm(`是否加入${selectedLiveTime}直播?`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        await liveApi.addToLive({
          goodsId: row.id,
          liveId: this.selectedLive
        })

        this.$message.success('加入直播成功')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('加入直播失败:', error)
        }
      }
    },

    // 导出商品
    async handleExport () {
      if (this.exporting) return

      this.exporting = true
      this.$message.info('正在导出商品，请稍候...')

      try {
        const response = await goodsApi.downloadAllGoods()
        const blob = new Blob([response], { type: 'application/vnd.ms-excel' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = '商品列表.xlsx'
        link.click()
        window.URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败，请稍后重试')
      } finally {
        this.exporting = false
      }
    },

    handleSelectionChange (selection) {
      console.log('选中的商品:', selection)
    },

    // 处理表格排序
    handleSortChange ({ column, prop, order }) {
      if (!prop || !order) {
        // 如果没有排序，保持原顺序
        return
      }

      this.goodsList.sort((a, b) => {
        const aVal = a[prop] || ''
        const bVal = b[prop] || ''
        
        // 处理空值
        if (!aVal && !bVal) return 0
        if (!aVal) return order === 'ascending' ? -1 : 1
        if (!bVal) return order === 'ascending' ? 1 : -1
        
        // 字符串比较
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          const result = aVal.localeCompare(bVal, 'zh-CN')
          return order === 'ascending' ? result : -result
        }
        
        // 数字比较
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return order === 'ascending' ? aVal - bVal : bVal - aVal
        }
        
        // 默认字符串比较
        const result = String(aVal).localeCompare(String(bVal), 'zh-CN')
        return order === 'ascending' ? result : -result
      })
    }
  }
}
</script>

<style scoped>
.goods-management {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}


/* 搜索卡片 */
.search-card {
  margin-bottom: 20px;
  border-radius: 12px;
}

.search-section {
  padding: 20px 0;
}

.search-input-group {
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-type-select {
  width: 120px;
}

.search-type-select .el-input__inner {
  border: none;
  border-right: 1px solid #E4E7ED;
  border-radius: 0;
}

.search-input {
  flex: 1;
}

.search-input .el-input__inner {
  border: none;
  border-radius: 0;
}

.search-button {
  border: none;
  border-radius: 0;
  background-color: #409EFF;
  color: white;
}

.search-button:hover {
  background-color: #66B1FF;
}

.live-select {
  width: 100%;
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

/* 表格卡片 */
.table-card {
  border-radius: 12px;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  border-bottom: 1px solid #EBEEF5;
  margin-bottom: 20px;
}

.table-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.table-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.table-count {
  background-color: #F4F4F5;
  color: #909399;
  border: none;
}

/* 表格样式 */
.goods-table {
  border-radius: 8px;
}

.brand-cell .brand-tag {
  background-color: #ECF5FF;
  color: #409EFF;
  border-color: #B3D8FF;
}

.name-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.product-name {
  font-weight: 500;
  color: #303133;
}

.product-shop {
  font-size: 12px;
  color: #909399;
}

.properties-cell .property-tag {
  background-color: #F4F4F5;
  color: #909399;
  border-color: #D3D4D6;
}

.empty-text {
  color: #C0C4CC;
  font-style: italic;
}

/* 展开内容 */
.demo-table-expand {
  font-size: 0;
}

.demo-table-expand label {
  width: 90px;
  color: #99a9bf;
}

.demo-table-expand .el-form-item {
  margin-right: 0;
  margin-bottom: 0;
  width: 50%;
}

/* 操作按钮 */
.action-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
}

.action-btn {
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 10px;
  transition: all 0.3s ease;
  min-width: auto;
  white-space: nowrap;
}

.view-btn {
  background-color: #ECF5FF;
  color: #409EFF;
  border-color: #B3D8FF;
}

.view-btn:hover {
  background-color: #409EFF;
  color: white;
}

.edit-btn {
  background-color: #F0F9FF;
  color: #67C23A;
  border-color: #B3E19D;
}

.edit-btn:hover {
  background-color: #67C23A;
  color: white;
}

.live-btn {
  background-color: #FDF6EC;
  color: #E6A23C;
  border-color: #F5DAB1;
}

.live-btn:hover {
  background-color: #E6A23C;
  color: white;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .goods-management {
    padding: 10px;
  }
  
  .search-section .el-row {
    .el-col {
      margin-bottom: 15px;
    }
  }
  
  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 2px;
  }
  
  .action-btn {
    width: 100%;
    font-size: 9px;
    padding: 2px 4px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/goods/GoodsList.vue